import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { api } from '@/services/api';

// Hidden super-admin tool. Not linked from the nav. Runs the one-shot in-place
// migration of legacy "maskedId == panel ID" rows into the proper HMAC shape.
// Idempotent — safe to hit repeatedly.
const AdminBackfillMaskedIdsPage = () => {
  const { user } = useAuth();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{ updated: number; skipped: number; total: number; message: string } | null>(null);
  const [error, setError] = useState<string>('');

  if (user && user.role !== 'super_admin') {
    return <Navigate to="/admin/projects" replace />;
  }

  const handleRun = async () => {
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const response = await (api as any).post('/admin/backfill-masked-ids');
      setResult(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Backfill failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-12 px-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-heading text-[#1A1F2B] mb-1">Masked-ID Backfill</h1>
        <p className="text-[14px] text-[#6B7280]">
          One-shot migration of legacy respondent rows whose <code className="font-mono text-[12px] bg-[#F5F7FA] px-1.5 py-0.5 rounded">masked_id</code> still equals
          the supplier panel ID. Rewrites them into the platform's HMAC format
          and encrypts the original panel ID into <code className="font-mono text-[12px] bg-[#F5F7FA] px-1.5 py-0.5 rounded">external_id</code>.
        </p>
        <p className="text-[13px] text-[#9CA3AF] mt-2">
          Idempotent — safe to run more than once; rows already in the new shape are skipped.
        </p>
      </div>

      <div className="md-card p-6">
        <button
          onClick={handleRun}
          disabled={running}
          className="md-btn md-btn-primary"
        >
          {running ? 'Running…' : 'Run backfill'}
        </button>

        {result && (
          <div className="mt-5 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <p className="text-[14px] font-semibold text-emerald-900 mb-2">{result.message}</p>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-white rounded-lg p-3 border border-emerald-100">
                <div className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Updated</div>
                <div className="text-2xl font-bold text-emerald-700">{result.updated}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-emerald-100">
                <div className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Skipped</div>
                <div className="text-2xl font-bold text-[#1A1F2B]">{result.skipped}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-emerald-100">
                <div className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Total</div>
                <div className="text-2xl font-bold text-[#1A1F2B]">{result.total}</div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-5 text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl">{error}</div>
        )}
      </div>
    </div>
  );
};

export default AdminBackfillMaskedIdsPage;
